// import components
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton
} from '@mui/material';
import { Tooltip } from 'components/shared/index';
import { StyledButtonBoxWrapper } from 'components/shared/TenantFooterBar/TenantFooterBar.styles';

// import hooks
import { useSessionStorage } from 'usehooks-ts';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

// import styles
import {
  StyledImageBox,
  StyledNewFeatureDialog,
  StyledNewFeatureFab,
  StyledNewFeatureFontAwesomeSvgIcon,
  StyledNewFeatureIconButton
} from './NewFeatureInformationDialog.styles';
import SpeechBubble from './components/SpeechBubble';

// import types
import { NewFeatureInformationDialogProps } from './NewFeatureInformationDialog.types';
import { Icon } from '@sdb/planning-ui';
import { StyledSmallerIcon } from '../Icon/Icon.styles';

const NewFeatureInformationDialog = ({
  title,
  tooltipTitle,
  content,
  uniqueFeatureName,
  speechBubbleText,
  showSpeechBubble = true,
  showCoverImage = false,
  coverImageUrl
}: NewFeatureInformationDialogProps) => {
  const [isHidden, setIsHidden] = useState(false);
  const [isFabClosing, setIsFabClosing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isNewFeatureInformationHidden, setIsNewFeatureInformationHidden] =
    useSessionStorage<boolean>(uniqueFeatureName, false);
  const [showAnimatedSpeechBubble, setShowAnimatedSpeechBubble] =
    useState(false);

  const handleOpenDialog = () => {
    setIsHidden(true);
  };

  const handleCloseDialog = () => {
    setIsHidden(false);
  };

  const handleCloseIcon = (event) => {
    event.stopPropagation();
    setIsFabClosing(true);
    setTimeout(() => {
      setIsNewFeatureInformationHidden(true);
    }, 600);
  };

  // listen for storage changes and update state
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === uniqueFeatureName) {
        const newValue = event.newValue === 'true';
        setIsNewFeatureInformationHidden(newValue);

        // If the session storage was cleared, reset isHidden to false
        if (!newValue) {
          setIsHidden(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (showSpeechBubble) {
      const showTimer = setTimeout(
        () => setShowAnimatedSpeechBubble(true),
        1500
      ); // show after 1s
      const hideTimer = setTimeout(
        () => setShowAnimatedSpeechBubble(false),
        8000
      ); // hide after 8s more

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [showSpeechBubble]);

  if (isNewFeatureInformationHidden) {
    return null;
  }

  return (
    <>
      <StyledNewFeatureDialog
        open={isHidden}
        onClose={handleCloseDialog}
        aria-labelledby="NewFeatureDialog-title"
        aria-describedby="NewFeatureDialog-description"
        maxWidth="medium"
      >
        {showCoverImage && (
          <StyledImageBox
            backgroundUrl={`${window?.reactMetadata?.staticUrl}${coverImageUrl}`}
          />
        )}
        <StyledNewFeatureIconButton
          aria-label={t('close')}
          onClick={handleCloseDialog}
        >
          <Icon type="xmark" fontSize="small" />
        </StyledNewFeatureIconButton>
        <DialogTitle id="NewFeatureDialog-title">{title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="NewFeatureDialog-description">
            <Trans i18nKey={content} components={{ br: <br />, a: <a /> }} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} autoFocus>
            {t('close')}
          </Button>
        </DialogActions>
      </StyledNewFeatureDialog>

      <SpeechBubble
        showAnimatedSpeechBubble={showAnimatedSpeechBubble}
        speechBubbleText={speechBubbleText}
      />

      <Tooltip title={tooltipTitle} placement="left">
        <StyledNewFeatureFab
          color="primary"
          aria-label={t('information')}
          onClick={handleOpenDialog}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          bounceOut={isFabClosing}
        >
          <StyledNewFeatureFontAwesomeSvgIcon
            type="hand-wave"
            color="inherit"
            fontSize="medium"
          />

          <StyledButtonBoxWrapper isHovered={isHovered}>
            <Tooltip title={`${t('hide')}`} arrow>
              <IconButton
                size="small"
                onClick={handleCloseIcon}
                component="span"
              >
                <StyledSmallerIcon type="xmark" color="inherit" />
              </IconButton>
            </Tooltip>
          </StyledButtonBoxWrapper>
        </StyledNewFeatureFab>
      </Tooltip>
    </>
  );
};

export default NewFeatureInformationDialog;
